import { Circle, Edit, Trash } from "lucide-react";
import { icons } from "lucide-react";
import { Flex, Text } from "@radix-ui/themes";
import Surface from "@/components/ui/Surface";
import { DragHandle } from "@/components/dnd";
import { MenuItem } from "@/types/breeze/Menu";
import LockedIcon from "@/components/ui/LockedIcon";
import { useConfirm } from "@/providers/ConfirmDialogProvider";

export default function MenuItemCard({
  item,
  onEdit,
  onDelete,
  dragHandleProps,
  additionalTitleContent,
}: {
  item: MenuItem;
  onEdit: (item: MenuItem) => void;
  onDelete: (id: number) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  additionalTitleContent?: React.ReactNode;
}) {
  const confirm = useConfirm();

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete Menu Item",
      message: `Are you sure you want to delete "${item.display_name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });
    if (confirmed) onDelete(item.id);
  };

  const IconComponent = icons[item.icon as keyof typeof icons] || Circle;

  const actions = item.locked
    ? []
    : [
        {
          label: "Edit",
          icon: <Edit style={{ width: "1rem", height: "1rem" }} />,
          onClick: () => onEdit(item),
        },
        {
          label: "Delete",
          icon: <Trash style={{ width: "1rem", height: "1rem" }} />,
          onClick: handleDelete,
          className: "text-red-600",
        },
      ];

  return (
    <Surface
      title={
        <Flex align="center" gap="2">
          <IconComponent
            style={{ width: "1rem", height: "1rem", color: "var(--gray-11)" }}
          />
          <Text weight="medium" size="2">
            {item.display_name}
          </Text>
        </Flex>
      }
      body={item.href}
      preTitleContent={
        dragHandleProps ? (
          <DragHandle {...dragHandleProps} disabled={item.locked} />
        ) : undefined
      }
      additionalTitleContent={
        <>
          {additionalTitleContent}
          {item.locked && <LockedIcon />}
        </>
      }
      actions={actions}
    />
  );
}
